import { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import { isEmpty, xor } from "lodash";

import { Button } from "@mui/material";
import ResetIcon from "@mui/icons-material/RotateLeft";

import p13nService from "@plannel/services/system/p13n-service";
import ColorUtil from "@plannel/pages/utils/color-util";
import BaseConfigModal from "@plannel/components/modal/BaseConfigModal";
import GroupedTokenSelect from "@plannel/components/ui/GroupedTokenSelect";
import Dialog from "@plannel/components/Dialog"
import Snackbar from "@plannel/components/Snackbar";
import debug from "@plannel/utils/debug-util";

const MODAL_TITLE = "dimensionSetting";
const DIALOG_TITLE = { SAVE: "SAVE", RESET: "RESET", CLOSE: "CLOSE" };
const { snackBarMessageColor } = ColorUtil;

const DimensionModal = (props) => {
  const { t, open, setOpen, viewName, allDimensions, applyDimensions, headerNameByField } = props;
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const [selectedDimension, setSelectedDimension] = useState([]);   // 저장돼 있는 값
  const [draft, setDraft] = useState([]);                           // 편집 중인 값

  // 열 때마다 저장값으로 되돌린다 — 취소하고 다시 열면 편집 흔적이 남으면 안 된다
  useEffect(() => {
    if (open) {
      setDraft(selectedDimension);
    }
  }, [open, selectedDimension]);

  const label = dimension => t(headerNameByField?.[dimension] ?? dimension);

  const handleClose = () => {
    setOpen(false);
  }

  const getDimensions = () => {
    p13nService.get(viewName)
      .then(response => {
        const { data } = response;
        const dimensions = data?.dimension ? data.dimension : allDimensions;
        setSelectedDimension(dimensions);
        applyDimensions(dimensions);
      })
      .catch(e => {
        debug(e);
      }).then(() => {
        handleClose();
      });
  }

  const saveDimensions = ({ selected }) => {
    const saveData = { pageCd: viewName, configObj: { dimension: selected } };

    p13nService.upsert(saveData)
      .then(() => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
      })
      .catch(e => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
        debug(e);
      })
      .then(() => {
        getDimensions();
      });

  }

  const isUpdated = selected => {
    if (selected.length !== selectedDimension.length) {
      return true;
    }

    const xorMeasure = xor(selected, selectedDimension);
    return !isEmpty(xorMeasure);
  }

  const handleDialogOpen = (action) => {
    // 저장 순서는 allDimensions 를 따른다 — 화면 컬럼 순서는 각 페이지의 defaultColumnDefs 가 정하므로
    // 선택 순서는 어차피 쓰이지 않는다(그래서 행 드래그도 걷어냈다)
    const selected = allDimensions.filter(dimension => draft.includes(dimension));

    if (action === DIALOG_TITLE.SAVE) {
      if (selected.length > 0) {
        setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm"), el: { selected } });
      } else {
        setSnackInfo({ open: true, severity: snackBarMessageColor.noMatchCondition, content: t("MSG_SelectLeastOneDimension") });
      }
    } else if (action === DIALOG_TITLE.RESET) {
      setDialogInfo({ open: true, title: "reset", severity: "info", content: t("MSG_ResetDimension"), el: { selected: allDimensions } });
    } else if (action === DIALOG_TITLE.CLOSE) {
      if (isUpdated(selected)) {
        setDialogInfo({ open: true, title: "close", content: t("MSG_ConfirmToClose") });
      } else {
        handleClose();
      }
    }
  }

  const handleDialog = (action, _falseAction, el) => {
    switch (action) {
      case DIALOG_TITLE.SAVE:
      case DIALOG_TITLE.RESET: {
        saveDimensions(el);
        break;
      }
      case DIALOG_TITLE.CLOSE: {
        handleClose();
        break;
      }
      default: break;
    }
  }

  // 그룹이 하나뿐이라 제목은 비워 둔다 — 헤더의 「Selected n / Select All」 이 옛 active 체크박스 줄을 대신한다
  const tokenGroups = [{
    key: "dimension",
    label: "",
    options: allDimensions.map(dimension => ({ value: dimension, label: label(dimension) })),
  }];

  useEffect(() => {
    getDimensions();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <>
    <BaseConfigModal
      open={open}
      onClose={() => handleDialogOpen(DIALOG_TITLE.CLOSE)}
      title={t(MODAL_TITLE)}
      onSave={() => handleDialogOpen(DIALOG_TITLE.SAVE)}
      extraActions={
        <Button size="small" startIcon={<ResetIcon />}
          onClick={() => handleDialogOpen(DIALOG_TITLE.RESET)}>
          {t("reset")}
        </Button>
      }
      size="md">
      {/* 라벨만 켜고 끄는 목록이라 체크박스가 아니라 토큰이다 — 규격은 toggle 스킬 §1-2 */}
      <GroupedTokenSelect groups={tokenGroups} value={draft} onChange={setDraft} />
    </BaseConfigModal>
    <Snackbar
      open={snackInfo.open}
      onClose={() => setSnackInfo({ ...snackInfo, open: false })}
      severity={snackInfo.severity}
      content={snackInfo.content}
    />
    <Dialog
      open={dialogInfo.open}
      onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
      title={dialogInfo.title}
      content={dialogInfo.content}
      onHandler={handleDialog}
      el={dialogInfo.el}
    />
  </>
}

export default withTranslation()(DimensionModal);
